import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ChatSidebar } from "./ChatSidebar";
import { ChatArea } from "./ChatArea";
import { useContactsPaginated, type ChatFilters } from "@/hooks/useContactsPaginated";
import { useMessagesPaginated } from "@/hooks/useMessagesPaginated";
import { useAuth } from "@/contexts/AuthContext";
import LeadManagementPanel from "./LeadManagementPanel";
import type { Database } from "@/lib/supabase";
import { type ChatFilterValues, defaultChatFilters } from "./ChatFilters";

type AccountContact = Database['public']['Tables']['account_contacts']['Row']

const ChatInterface = () => {
  const { contactId } = useParams<{ contactId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [chatFilters, setChatFilters] = useState<ChatFilterValues>(defaultChatFilters);
  const filters: ChatFilters = {
    attributionId: chatFilters.attributionId,
    statusChat: chatFilters.statusChat,
    tag: chatFilters.tag,
  };
  const { contacts, loading: contactsLoading, hasMore: hasMoreContacts, loadMore: loadMoreContacts, getContactById, refetch: refetchContacts, updateContactLocally } = useContactsPaginated(filters)
  const [selectedContact, setSelectedContact] = useState<AccountContact | null>(null)
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null)
  const [message, setMessage] = useState<string>("")

  // Refs para prevenir race conditions
  const contactIdRef = useRef<string | null>(null)
  const isNavigatingRef = useRef(false)
  const {
    messages,
    loading: messagesLoading,
    hasMore,
    isLoadingMore,
    loadMoreMessages,
    sendMessage,
    sendAudio,
    sendImage,
    sendDocument,
    sending
  } = useMessagesPaginated(selectedContact)

  const handleContactUpdate = async () => {
    // Atualizar lista de contatos
    await refetchContacts()
    // O useEffect abaixo vai atualizar o selectedContact automaticamente quando contacts mudar
  }

  const handleContactUpdateOptimistic = useCallback((updates: Partial<AccountContact>) => {
    if (!selectedContact) return;

    console.log('⚡ [ChatInterface] Optimistic update for contact:', selectedContact.id);

    // Atualizar contato selecionado imediatamente
    setSelectedContact(prev => prev ? { ...prev, ...updates } : null);

    // Atualizar na lista de contatos
    updateContactLocally(selectedContact.id, updates);
  }, [selectedContact, updateContactLocally]);

  // Selecionar contato pela URL (contactId)
  useEffect(() => {
    if (!contactId || contactsLoading) return;

    // Armazenar contactId atual
    contactIdRef.current = contactId

    const loadContact = async () => {
      console.log('🔍 [ChatInterface] Looking for contact by ID:', contactId);

      // Verificação ANTES de buscar para evitar fetch desnecessário
      if (contactIdRef.current !== contactId) {
        console.log('⚠️ [ChatInterface] ContactId changed before fetch, aborting');
        return;
      }

      const contact = await getContactById(contactId);

      // Verificação crítica: Só aplicar se contactId ainda é o mesmo
      if (contactIdRef.current !== contactId) {
        console.log('⚠️ [ChatInterface] ContactId changed during load, ignoring result');
        return;
      }

      if (contact) {
        console.log('✅ [ChatInterface] Found contact:', contact.jid);
        setSelectedContact(contact);
        setSelectedContactId(contact.id);
      } else {
        console.warn('⚠️ [ChatInterface] Contact not found for ID:', contactId);
      }
    };

    loadContact();
  }, [contactId, contactsLoading, getContactById]);

  // Selecionar primeiro contato se não houver contactId na URL
  useEffect(() => {
    if (!contactId && contacts.length > 0 && !selectedContactId) {
      console.log('🎯 [ChatInterface] No contactId in URL, auto-selecting first contact')
      const firstContact = contacts[0];
      navigate(`/chat/${firstContact.id}`, { replace: true });
    }
  }, [contactId, selectedContactId, navigate]);

  // Atualizar selectedContact quando a lista de contatos mudar
  useEffect(() => {
    if (selectedContactId && contacts.length > 0) {
      const updatedContact = contacts.find(c => c.id === selectedContactId)
      if (updatedContact) {
        // Só atualizar se dados realmente mudaram
        setSelectedContact(prev => {
          if (!prev) return updatedContact;

          // Comparar campos relevantes
          const hasChanges =
            prev.name !== updatedContact.name ||
            prev.display_name !== updatedContact.display_name ||
            prev.push_name_last !== updatedContact.push_name_last ||
            prev.last_message_at !== updatedContact.last_message_at;

          if (hasChanges) {
            console.log('🔄 [ChatInterface] Updating selected contact data')
            return updatedContact;
          }

          return prev;
        });
      }
    }
  }, [contacts, selectedContactId])

  const handleContactSelect = useCallback((contact: AccountContact) => {
    // Prevenir cliques duplos
    if (isNavigatingRef.current) {
      console.log('⏸️ [ChatInterface] Navigation in progress, ignoring click');
      return;
    }

    console.log('👆 [ChatInterface] Click on contact:', contact.jid, '| Current ref:', contactIdRef.current, '| Navigating:', isNavigatingRef.current);

    isNavigatingRef.current = true;
    contactIdRef.current = contact.id;

    navigate(`/chat/${contact.id}`);
    setMessage("");

    // Resetar flag após navegação - reduzido para 50ms
    setTimeout(() => {
      isNavigatingRef.current = false;
    }, 50);
  }, [navigate]);

  const handleSendMessage = async () => {
    if (!selectedContact || !message.trim()) return

    const messageToSend = message.trim()
    setMessage("")

    const success = await sendMessage(selectedContact.jid, messageToSend)
    if (!success) {
      setMessage(messageToSend)
    }
  }

  const handleSendAudio = async (audioUrl: string) => {
    if (!selectedContact) return false

    const success = await sendAudio(selectedContact.jid, audioUrl)
    return success
  }

  const handleSendImage = async (imageBase64: string, caption?: string, fileName?: string) => {
    if (!selectedContact) return false

    const success = await sendImage(selectedContact.jid, imageBase64, caption, fileName)
    return success
  }

  const handleSendDocument = async (documentBase64: string, fileName: string, caption?: string) => {
    if (!selectedContact) return false

    const success = await sendDocument(selectedContact.jid, documentBase64, fileName, caption)
    return success
  }

  return (
    <div className="flex flex-col md:flex-row gap-4 w-full h-full">
      <ChatSidebar
        contacts={contacts}
        selectedContact={selectedContact}
        onContactSelect={handleContactSelect}
        loading={contactsLoading}
        hasMore={hasMoreContacts}
        onLoadMore={loadMoreContacts}
        filters={chatFilters}
        onFiltersChange={setChatFilters}
      />
      <ChatArea
        selectedContact={selectedContact}
        messages={messages}
        message={message}
        setMessage={setMessage}
        onSendMessage={handleSendMessage}
        onSendAudio={handleSendAudio}
        onSendImage={handleSendImage}
        onSendDocument={handleSendDocument}
        loading={messagesLoading}
        hasMore={hasMore}
        isLoadingMore={isLoadingMore}
        onLoadMore={loadMoreMessages}
        sending={sending}
        instance={(user?.account as any)?.instance || undefined}
        apiKey={(user?.account as any)?.api_token || undefined}
        onContactUpdate={handleContactUpdate}
        onContactUpdateOptimistic={handleContactUpdateOptimistic}
      />
      <LeadManagementPanel selectedContact={selectedContact} onContactUpdate={handleContactUpdate} />
    </div>
  );
};

export default ChatInterface;
